import '../../../../../../assets/css/autocomplete_input.css';
import marker from '../../../../../../assets/icons/ic_marker.svg';
import locationMarker from '../../../../../../assets/icons/ic_my_location.svg';
import { useDictionary } from '../../../../../../utilities/hooks/UseDictionary';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import dotenv from 'dotenv';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState, useRef } from 'react';
import React from 'react';
import AutocompleteInput from "../../../../common/google_maps_api/AutocompleteInput";
import AdvancedMarker from "../../../../common/google_maps_api/AdvancedMarker";
import { fetchMeetingPointPlaceFromCoordinates } from "../../../../common/google_maps_api/networking";

dotenv.config();

const LIBRARIES = ['places', 'marker'];

const MeetingPoint = ({ enabled, setEnabled, place, setPlace, directions, setDirections, experienceId }) => {
	const centerDefaultValue = { lat: 38.0068661, lng: 23.6950456 };
	const dictionary = useDictionary();

	// Map related
	const mapRef = useRef(null);
	const [zoom, setZoom] = useState(7);
	const [center, setCenter] = useState(centerDefaultValue);

	// Markers
	const [placeMarker, setPlaceMarker] = useState(null);
	const [userMarker, setUserMarker] = useState(null);
	// Markers content (image)
	const [placeMarkerContent, setPlaceMarkerContent] = useState(null);
	const [myPositionMarkerContent, setMyPositionMarkerContent] = useState(null);


	// Load Google Maps API with `mapId`
	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
		libraries: LIBRARIES,
		mapIds: [process.env.REACT_APP_GOOGLE_MAP_ID]
	});

	// Get user's location
	useEffect(() => {
		if (!isLoaded) return;

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const userLocation = {
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				};
				setUserMarker(userLocation);

				// If no place is selected (center has the default values), center on user location
				setCenter((prevCenter) =>
					prevCenter.lat === centerDefaultValue.lat && prevCenter.lng === centerDefaultValue.lng
						? userLocation
						: prevCenter
				);
			},
			(error) => console.warn("Geolocation error:", error)
		);
	}, [isLoaded]);

	// Update the place marker when switching tabs
	useEffect(() => {
		if (!enabled) {
			setPlaceMarker(null); // Clear marker when leaving tab
			return;
		}

		if (place?.location) {
			const newPlaceMarker = {
				lat: place.location.latitude,
				lng: place.location.longitude,
			};
			setPlaceMarker(newPlaceMarker);
			setCenter(newPlaceMarker);
			setZoom(16);
		}
	}, [place?.location, enabled]);

	// Set Markers Content - Image
	useEffect(() => {
		const placeImg = new Image();
		placeImg.src = marker;
		setPlaceMarkerContent(placeImg);

		const myPosImg = new Image();
		myPosImg.src = locationMarker;
		setMyPositionMarkerContent(myPosImg);
	}, []);

	// Handle Google Map Load
	const onLoad = useCallback((mapInstance) => {
		if (isLoaded) {
			mapRef.current = mapInstance;
		}
	}, [isLoaded]);

	// Drag Event - Update Place on Drag
	const onDragEnd = async (newPointer) => {
		fetchGeocodeData(newPointer.latLng.lat(), newPointer.latLng.lng()).then(response => {
			setPlace(response.data);
		});
	};

	// Fetch place details from backend
	const fetchGeocodeData = async (latitude, longitude) => {
		try {
			return await fetchMeetingPointPlaceFromCoordinates(latitude, longitude, experienceId);
		} catch (error) {
			console.error("Error fetching geocode data:", error);
		}
	};

	// Update `setEnabled` when `place` is selected
	useEffect(() => {
		setEnabled(!!place);
	}, [place, setEnabled]);

	return (
		<div className="new-experience-form-wrapper">
			<div className="new-experience-form-container thin-scrollbar-trans">
				<h2>{dictionary.where_should_meet}</h2>
				<div className="text">{dictionary.add_experience_point}</div>

				<div className="group-inputs">
					<AutocompleteInput setPlace={setPlace} experienceId={experienceId}/>
					{/* Display place details */}
					{place && (
						<div className="place-details">
							<h3>{place?.displayName?.text || "Unmatched address "}</h3>
							<p>{place?.formattedAddress || "N/A"}</p>
						</div>
					)}
				</div>

				{isLoaded && (
					<div className="experience-map" data-testid="map">
						<GoogleMap
							zoom={zoom}
							center={center}
							onLoad={onLoad}
							options={{
								zoomControl: false,
								streetViewControl: false,
								mapId: process.env.REACT_APP_GOOGLE_MAP_ID
							}}
							mapContainerClassName="experience-map"
						>
							{/* Place Marker */}
							{placeMarker && (
								<AdvancedMarker
									map={mapRef.current}
									position={placeMarker}
									title="Meeting Point"
									content={placeMarkerContent}
									draggable={true}
									onDragEnd={onDragEnd}
								/>
							)}

							{/* User Location Marker */}
							{userMarker && (
								<AdvancedMarker
									map={mapRef.current}
									position={userMarker}
									title="My Location"
									content={myPositionMarkerContent}
								/>
							)}
						</GoogleMap>
					</div>
				)}

				<div style={{ marginTop: '15px' }} className="text">
					{dictionary.directions}
				</div>
				<textarea
					style={{ marginBottom: '20px' }}
					value={directions ? directions : ''}
					onChange={(e) => setDirections(e.target.value)}
					placeholder={dictionary.please_provide_directions}
					data-testid="directions-input"
				/>
			</div>
		</div>
	);
};

MeetingPoint.propTypes = {
	setPlace: PropTypes.func.isRequired,
	setEnabled: PropTypes.func.isRequired,
	setDirections: PropTypes.func.isRequired,
	directions: PropTypes.string,
	place: PropTypes.object,
};

export default MeetingPoint;
